import { useEffect, useMemo, useState } from 'react'
import { Eye, FileText, Plus, RefreshCw, Upload } from 'lucide-react'
import { toast } from 'sonner'
import { TabsContent } from '../ui/tabs'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Input } from '../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { SearchFilter } from '../ui/SearchFilter'
import { GlobalPaginator } from '../ui/global-paginator'
import { DiscreteLoader } from '../ui/discrete-loader'
import { purchasesService, PurchaseRecord } from '../../services/purchases.service'
import { supplierService, SupplierItem } from '../../services/supplier.service'
import { useSessionStore } from '../../store/auth/auth.store'
import { DetailModal } from './modals/DetailModal'

interface ProductEntriesTabProps {
  refreshKey: number
  openXmlEntry: () => void
  openManualEntry: () => void
}

const perPage = 10

const money = (value: string | null, currency = 'MXN') => value === null
  ? 'Sin capturar'
  : new Intl.NumberFormat('es-MX', { style: 'currency', currency }).format(Number(value))

export function ProductEntriesTab({ refreshKey, openXmlEntry, openManualEntry }: ProductEntriesTabProps) {
  const canAction = useSessionStore((state) => state.canAction)
  const canCreate = canAction('purchases.purchase.create')
  const [entries, setEntries] = useState<PurchaseRecord[]>([])
  const [suppliers, setSuppliers] = useState<SupplierItem[]>([])
  const [search, setSearch] = useState('')
  const [supplierId, setSupplierId] = useState('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(1)
  const [retryKey, setRetryKey] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [pagination, setPagination] = useState({ total: 0, from: 0, to: 0 })
  const [detailOpen, setDetailOpen] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [selectedEntry, setSelectedEntry] = useState<PurchaseRecord | null>(null)

  useEffect(() => {
    let mounted = true
    supplierService.getItems({ all: true }).then((response) => {
      if (!mounted) return
      const data = Array.isArray(response.data) ? response.data : response.data.data
      setSuppliers(data)
    }).catch(() => {
      if (mounted) setSuppliers([])
    })
    return () => { mounted = false }
  }, [])

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setError(false)

    purchasesService.getProductEntries({
      search: search || undefined,
      supplier_id: supplierId === 'all' ? undefined : Number(supplierId),
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      page,
      per_page: perPage,
    }).then((response) => {
      if (!mounted) return
      setEntries(response.data.data)
      setPagination({
        total: response.data.total,
        from: response.data.from ?? 0,
        to: response.data.to ?? 0,
      })
    }).catch(() => {
      if (!mounted) return
      setEntries([])
      setPagination({ total: 0, from: 0, to: 0 })
      setError(true)
    }).finally(() => {
      if (mounted) setLoading(false)
    })

    return () => { mounted = false }
  }, [dateFrom, dateTo, page, refreshKey, retryKey, search, supplierId])

  const supplierOptions = useMemo(
    () => [...suppliers].sort((a, b) => a.name.localeCompare(b.name)),
    [suppliers],
  )

  const openDetail = async (entry: PurchaseRecord) => {
    setDetailOpen(true)
    setDetailLoading(true)
    setSelectedEntry(entry)
    try {
      const response = await purchasesService.getProductEntry(entry.id)
      setSelectedEntry(response.data)
    } catch {
      setDetailOpen(false)
      setSelectedEntry(null)
      toast.error('No fue posible cargar el detalle del ingreso')
    } finally {
      setDetailLoading(false)
    }
  }

  return (
    <TabsContent value="products" className="space-y-4">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="text-sm text-slate-600">{pagination.total} ingresos registrados en esta sucursal</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <SearchFilter
            className="w-full sm:w-64"
            placeholder="Nota, producto o proveedor..."
            onSearch={(value) => { setSearch(value); setPage(1) }}
          />
          <Select value={supplierId} onValueChange={(value) => { setSupplierId(value); setPage(1) }}>
            <SelectTrigger className="w-full sm:w-52"><SelectValue placeholder="Proveedor" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los proveedores</SelectItem>
              {supplierOptions.map((supplier) => <SelectItem key={supplier.id} value={String(supplier.id)}>{supplier.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={dateFrom} onChange={(event) => { setDateFrom(event.target.value); setPage(1) }} className="w-auto" aria-label="Fecha inicial" />
          <Input type="date" value={dateTo} onChange={(event) => { setDateTo(event.target.value); setPage(1) }} className="w-auto" aria-label="Fecha final" />
          {canCreate && (
            <>
              <Button type="button" variant="outline" onClick={openXmlEntry}>
                <Upload className="mr-2 h-4 w-4" /> Cargar XML de Factura
              </Button>
              <Button type="button" onClick={openManualEntry}>
                <Plus className="mr-2 h-4 w-4" /> Agregar sin XML
              </Button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="min-h-64 rounded-lg border bg-white"><DiscreteLoader text="Cargando productos ingresados..." /></div>
      ) : error ? (
        <div className="flex min-h-64 flex-col items-center justify-center rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <p className="font-medium text-red-800">No fue posible cargar los ingresos</p>
          <Button variant="outline" className="mt-4" onClick={() => setRetryKey((value) => value + 1)}>
            <RefreshCw className="mr-2 h-4 w-4" /> Reintentar
          </Button>
        </div>
      ) : entries.length === 0 ? (
        <div className="flex min-h-64 flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center">
          <FileText className="mb-3 h-10 w-10 text-slate-300" />
          <p className="font-medium text-slate-700">No se encontraron ingresos</p>
          <p className="mt-1 text-sm text-slate-500">Ajusta los filtros o registra la primera nota manual.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1050px]">
              <thead className="border-b bg-slate-50 text-left text-xs font-medium uppercase text-slate-700">
                <tr>
                  <th className="px-4 py-3">Folio</th>
                  <th className="px-4 py-3">Origen</th>
                  <th className="px-4 py-3">Proveedor</th>
                  <th className="px-4 py-3">Fecha compra</th>
                  <th className="px-4 py-3">Fecha ingreso</th>
                  <th className="px-4 py-3">Forma de pago</th>
                  <th className="px-4 py-3">Productos</th>
                  <th className="px-4 py-3">Total</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-slate-50">
                    <td className="px-4 py-4 font-medium">{entry.reference_number}</td>
                    <td className="px-4 py-4"><Badge variant="outline">{entry.source === 'excel' ? 'Excel' : 'Manual / Sin XML'}</Badge></td>
                    <td className="px-4 py-4">{entry.supplier.name}</td>
                    <td className="px-4 py-4">{entry.purchase_date}</td>
                    <td className="px-4 py-4">{entry.entry_date}</td>
                    <td className="px-4 py-4">{entry.payment_condition === 'cash' ? 'Contado' : 'Crédito'}</td>
                    <td className="px-4 py-4">{entry.items_count}</td>
                    <td className="px-4 py-4 font-semibold">{money(entry.total_amount, entry.currency)}</td>
                    <td className="px-4 py-4"><Badge className="bg-green-100 text-green-700">Procesado</Badge></td>
                    <td className="px-4 py-4">
                      <button type="button" onClick={() => void openDetail(entry)} className="rounded p-1 hover:bg-slate-100" title="Ver detalle">
                        <Eye className="h-4 w-4 text-slate-600" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <GlobalPaginator
            totalItems={pagination.total}
            itemsPerPage={perPage}
            currentPage={page}
            from={pagination.from}
            to={pagination.to}
            onPageChange={setPage}
          />
        </div>
      )}

      <DetailModal
        showDetailModal={detailOpen}
        setShowDetailModal={setDetailOpen}
        selectedEntry={selectedEntry}
        isLoading={detailLoading}
      />
    </TabsContent>
  )
}
